// useDisplayZone — the ONE zone a screen shows its times in, and the word for it
// (ADR-082 §2). Seats: `project` (the hub, the registers, the lists — every
// project screen), `site` (a site's own tabs; a draft without a zone reads in the
// project's), `person` (profile · tokens · sessions · the platform pages — the
// viewer's own, never a word). The word is RELATIONAL — "project time" · "site
// time" · "UTC" — said only to a viewer whose own zone differs BY NAME: an offset
// is not a zone (two names with one offset are two zones, and a clock change can
// part them); beside the word the caption says the DIFFERENCE to the viewer's
// own clock right now ("2 h ahead of you"), the thing a person actually
// needs — and nothing at all while the two clocks agree. Nothing here reads a browser's zone; the
// viewer's is `/me`'s.
//
// The project read is the frame's (ThemeProvider holds it), so `zone` is
// normally there on the first paint; a caller that must cut days before it lands
// falls back to the viewer's own — a stand-in for a frame (UTC only as the last
// resort behind it, before `/me` is in). A zone the runtime cannot format in
// reads as UTC rather than throwing on every row (`knownZone`).

import type { MeResponse } from '@ems-app/schemas/auth/responses';
import { knownZone, zoneDifferenceNow } from '../lib/format.js';
import { useMe } from './useMe.js';
import { useProject } from './useProject.js';

export type DisplayZoneSeat =
  | { kind: 'project'; projectId: string }
  | { kind: 'site'; projectId: string; zone: string | null }
  | { kind: 'person' };

/** The relational word — `null` when the viewer's own zone is the screen's (nothing to say). */
export type ZoneWord = 'project' | 'site' | 'utc' | null;

export interface DisplayZone {
  /** The zone the screen's times render in; undefined while the project read is in flight. */
  zone: string | undefined;
  word: ZoneWord;
  /** The screen zone's clock minus the viewer's, in minutes, right now — said beside the word; null when there is no word (or a clock is unknown). */
  difference: number | null;
  /** The viewer's own zone (`/me`). */
  viewerZone: string | undefined;
}

const selectViewerZone = (m: MeResponse) => m.user.timezone_iana;

/** The word for a screen zone seen by a viewer — by NAME, never by offset. */
export function zoneWordFor(seat: DisplayZoneSeat['kind'], zone: string | undefined, viewerZone: string | undefined, siteZone: string | null = null): ZoneWord {
  if (seat === 'person' || zone === undefined || viewerZone === undefined || zone === viewerZone) return null;
  if (seat === 'site' && siteZone) return 'site';
  return zone === 'UTC' ? 'utc' : 'project';
}

export function useDisplayZone(seat: DisplayZoneSeat): DisplayZone {
  const { data: viewerZone } = useMe(selectViewerZone);
  const { data: project } = useProject(seat.kind === 'person' ? '' : seat.projectId);
  if (seat.kind === 'person') return { zone: viewerZone, word: null, difference: null, viewerZone };
  const zone = knownZone(seat.kind === 'site' && seat.zone ? seat.zone : project?.timezone_iana);
  const word = zoneWordFor(seat.kind, zone, viewerZone, seat.kind === 'site' ? seat.zone : null);
  return { zone, word, difference: word && zone && viewerZone ? zoneDifferenceNow(zone, viewerZone) : null, viewerZone };
}
